import { keyframes, css } from '@emotion/react';
import styled from '@emotion/styled';
import type { OverlayEffectStyleCustomizationRequest } from '@zola/svc-web-api-ts-client';
import { SPACING } from '@zola/zola-ui/src/styles/emotion';

const show = keyframes`
0% { 
  opacity: 0;
}

100% {
  opacity: 1;
}
`;

export const StyledOverlayWrapper = styled.div<{
  delay: number;
  isSinglePage?: boolean;
  heightPercentage: number;
  position?: OverlayEffectStyleCustomizationRequest.PositionEnum;
  isMonogram?: boolean;
}>`
  z-index: 101; // hero z-index is 100
  animation: ${show} ${props => props.delay}s;
  position: absolute;
  height: 100%;
  display: flex;
  flex-direction: column;
  top: ${props => (props.isSinglePage ? 0 : 'auto')};
  width: ${props => (props.isSinglePage ? '100%' : 'auto')};
  padding: ${SPACING.MD};
  
  path { 
    fill: inherit;
    stroke: ${props => (props.isMonogram ? '' : 'inherit')};
  }
 

  > div {
    height: ${props => props.heightPercentage}%;
  }

  ${props =>
    (props.position as unknown) === 'TOP_LEFT' &&
    css`
      left: 0;
      top: 0;
      align-items: ${props.isSinglePage ? 'flex-start' : 'auto'};
    `}

  ${props =>
    (props.position as unknown) === 'TOP_RIGHT' &&
    css`
      right: 0;
      top: 0;
      align-items: ${props.isSinglePage ? 'flex-end' : 'auto'};
    `}

  ${props =>
    (props.position as unknown) === 'LEFT' &&
    css`
      left: 0;
      justify-content: center;
      align-items: ${props.isSinglePage ? 'flex-start' : 'auto'};
    `}

    ${props =>
      (props.position as unknown) === 'CENTER' &&
      css`
        right: auto !important;
        left: auto;

        justify-content: center;
      `}

    ${props =>
      (props.position as unknown) === 'RIGHT' &&
      css`
        right: 0;
        justify-content: center;
        align-items: ${props.isSinglePage ? 'flex-end' : 'auto'};
      `}

  ${props =>
    (props.position as unknown) === 'BOTTOM_LEFT' &&
    css`
      left: 0;
      justify-content: end;
      align-items: ${props.isSinglePage ? 'flex-start' : 'auto'};
    `}


  ${props =>
    (props.position as unknown) === 'BOTTOM_CENTER' &&
    css`
      justify-content: end;
    `}


  ${props =>
    (props.position as unknown) === 'BOTTOM_RIGHT' &&
    css`
      right: 0;
      justify-content: end;
      align-items: ${props.isSinglePage ? 'flex-end' : 'auto'};
    `}
`;

export const StyledBridlewoodWrapper = styled(StyledOverlayWrapper)`
  // for some reason, sometimes lottie renders the date and year with an extra empty text element, which causes the positions of the characters to be incorrect
  // so we have to manually set the positions
  g {
    // date
    &:nth-of-type(3) {
      text {
        transform: matrix(1, 0, 0, 1, 2.460179090499878, 44.61082077026367);
        &:first-of-type {
          transform: matrix(1, 0, 0, 1, -46.28486633300781, 44.61082077026367);
        }
      }
    }

    // year
    &:nth-of-type(5) {
      text {
        transform: matrix(1, 0, 0, 1, 22.141847610473633, 19.291358947753906);

        &:first-of-type {
          transform: matrix(1, 0, 0, 1, -45, 19.291358947753906);
        }
        &:nth-of-type(2) {
          transform: matrix(1, 0, 0, 1, -23.466894149780273, 19.291358947753906);
        }
        &:nth-of-type(3) {
          transform: matrix(1, 0, 0, 1, 0.6087455153465271, 19.291358947753906);
        }
      }
    }
  }

  ${props =>
    props.isSinglePage &&
    css`
      ${((props.position as unknown) === 'BOTTOM_CENTER' ||
        (props.position as unknown) === 'CENTER' ||
        (props.position as unknown) === 'TOP_CENTER') &&
      css`
        align-items: center;
      `}
      padding: 24px 0;
      > div {
        width: ${props.heightPercentage}%;
        height: auto;
      }
    `}
`;

export const StyledGlenardenWrapper = styled.div<{
  delay: number;
  isSinglePage?: boolean;
  heightPercentage: number;
}>`
  z-index: 101; // hero z-index is 100
  animation: ${show} ${props => props.delay}s;
  position: absolute;
  bottom: 0;
  height: ${props => props.heightPercentage}%;

  display: flex;
  justify-content: center;
  ${props =>
    props.isSinglePage &&
    css`
      height: auto;

      > div {
        width: ${props.heightPercentage}%;
      }
    `}
`;

export const StyledViallTopRightWrapper = styled(StyledOverlayWrapper)`
  right: 0;

  ${props =>
    props.isSinglePage &&
    css`
      width: auto;
    `}
`;

export const StyledViallBottomLeftWrapper = styled(StyledOverlayWrapper)`
  left: 0;

  justify-content: flex-end;

  ${props =>
    props.isSinglePage &&
    css`
      width: auto;
    `}
`;
